import React, { useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import { FiInfo } from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import NotificationsActions from '~redux/notifications';

import PromptService from '~services/prompt';
import { useStateFromProp } from '~hooks/common';

import Modal from '~components/modal';
import Button from '~components/button';

import { UserContext } from '~contexts/user';
import { AuthContext } from '~contexts/auth';

import './style.scss';

const ActionsModal = ({ isOpened, onToggle = () => {} }) => {
  const intl = useIntl();
  const [isModalOpened, setModalOpen] = useStateFromProp(isOpened, onToggle);

  return (
    <Modal.Wrapper isOpened={isModalOpened} onClose={() => setModalOpen()} className="c-modal--plain c-modal--narrow">
      <Modal.Content>
        <h2 className="l-headline--secondary c-modal__headline">
          {intl.formatMessage({
            id: 'containers.account.actions.modal.headline',
            defaultMessage: 'Suspend my offer',
          })}
        </h2>
        <p>
          {intl.formatMessage({
            id: 'containers.account.actions.modal.content',
            defaultMessage:
              'You can suspend your offer and restore it later. After you suspend your offer will no longer be presented to other users. You can unsuspend your offer at any time.',
          })}
        </p>
      </Modal.Content>
    </Modal.Wrapper>
  );
};

const Actions = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { user, updateWorkerParam, updateCustomerParam, deleteUser, deleteUserPicture } = useContext(UserContext);
  const { signOut } = useContext(AuthContext);
  const { personId, accountType, wish, operations } = user;

  const isPublished = accountType === 'worker' ? operations.published : wish.published;
  const isHidden = accountType === 'worker' ? operations.is_hidden : wish.is_hidden;

  const isOfferCreated =
    accountType === 'worker'
      ? !!(operations?.address && operations?.address?.place_id)
      : !!(wish?.address && wish?.address?.place_id);

  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isArchivingAccount, setIsArchivingAccount] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const archiveAccount = async (action = 'suspend') => {
    console.log(action)
    try {
      setIsArchivingAccount(true);
      const published = action !== 'suspend';
      const is_hidden = !published;

      if (accountType === 'worker') {
        const newUser = { ...user, operations: { ...operations, published, is_hidden } };
        await updateWorkerParam(personId, { published }, newUser);
      } else {
        const newUser = { ...user, wish: { ...wish, published, is_hidden } };
        await updateCustomerParam(personId, { published }, newUser);
      }

      dispatch(
        NotificationsActions.addNotification(
          intl.formatMessage({
            id: `containers.account.actions.${action}-notification`,
            defaultMessage: 'You offer has been suspended',
          })
        )
      );
    } catch (err) {
      console.log('Error while archiving account');
      PromptService.error(err);
    } finally {
      setIsArchivingAccount(false);
    }
  };

  const showArchiveAccountPrompt = () => {
    if (isArchivingAccount) {
      return;
    }

    // const action = isPublished ? 'suspend' : 'unsuspend';
    const action = isHidden ? 'unsuspend' : 'suspend';

    if (action === 'unsuspend') {
      archiveAccount(action);
      return;
    }

    PromptService.confirm({
      title: intl.formatMessage({
        id: `containers.account.actions.suspend-prompt.title`,
        defaultMessage: 'Are you sure?',
      }),
      content: intl.formatMessage({
        id: `containers.account.actions.suspend-prompt.content`,
        defaultMessage: 'Are you sure you want to suspend your offer?',
      }),
      ok: intl.formatMessage({ id: `containers.account.actions.suspend-prompt.ok`, defaultMessage: 'OK' }),
      cancel: intl.formatMessage({ id: `containers.account.actions.suspend-prompt.cancel`, defaultMessage: 'Cancel' }),
    }).then((response) => {
      if (typeof response === 'undefined') {
        return;
      }

      archiveAccount(action);
    });
  };

  const deleteAccount = async () => {
    setIsDeletingAccount(true);

    try {
      await Promise.all([deleteUserPicture(personId), deleteUser(personId)]);
      dispatch(
        NotificationsActions.addNotification(
          intl.formatMessage({
            id: 'containers.account.actions.delete-notification',
            defaultMessage: 'You account has been deleted',
          })
        )
      );
      signOut();
    } catch (err) {
      PromptService.error(err);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const showDeleteAccountPrompt = () => {
    if (isDeletingAccount) {
      return;
    }

    PromptService.confirm({
      title: intl.formatMessage({
        id: 'containers.account.actions.delete-prompt.title',
        defaultMessage: 'Are you sure?',
      }),
      content: intl.formatMessage({
        id: 'containers.account.actions.delete-prompt.content',
        defaultMessage:
          'Are you sure you want to delete your account? All your data will be removed and you will be logged out.',
      }),
      ok: intl.formatMessage({ id: 'containers.account.actions.delete-prompt.ok', defaultMessage: 'Delete Account' }),
      cancel: intl.formatMessage({ id: 'containers.account.actions.delete-prompt.cancel', defaultMessage: 'Cancel' }),
      callback: () => deleteAccount(),
    });
  };

  return (
    <div className="c-box">
      <div className="c-box__content">
        <div className="c-editable">
          <div className="c-editable__content c-account-actions__container">
            {isOfferCreated && (
              <div className="c-suspend-button">
                <Button
                  className="c-btn--text"
                  onClick={() => showArchiveAccountPrompt()}
                  isLoading={isArchivingAccount}
                >
                  {/* {isHidden
                    ? intl.formatMessage({
                        id: 'containers.account.actions.suspend-offer',
                        defaultMessage: 'Suspend my offer',
                      })
                    : intl.formatMessage({
                        id: 'containers.account.actions.unsuspend-offer',
                        defaultMessage: 'Unsuspend my offer',
                      })} */}
                  
                  {isHidden
                    ? intl.formatMessage({
                        id: 'containers.account.actions.unsuspend-offer',
                        defaultMessage: 'Unsuspend my offer',
                      })
                    : intl.formatMessage({
                        id: 'containers.account.actions.suspend-offer',
                        defaultMessage: 'Suspend my offer',
                      })}
                </Button>
                <button type="button" className="c-account-actions__info-btn" aria-label={intl.formatMessage({ id: 'common.more-info', defaultMessage: 'More information' })} onClick={() => setIsModalOpened(true)}>
                  <FiInfo className="c-account-actions__info" aria-hidden="true" />
                </button>
              </div>
            )}

            <Button className="c-btn--text c-delete-account-button" onClick={() => showDeleteAccountPrompt()}>
              {intl.formatMessage({
                id: 'containers.account.actions.delete-account',
                defaultMessage: 'Delete account',
              })}
            </Button>
          </div>
        </div>
      </div>
      <ActionsModal isOpened={isModalOpened} onToggle={setIsModalOpened} />
    </div>
  );
};

export default Actions;
